import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { FileUp, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { VehicleServiceStatus, VehicleServiceType } from '../../../../shared/types/vehicle-service.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleService from '../../wrappers/use-vehicle-service';
import useSettingLists from '../../wrappers/use-setting-list-item';
import useFile from '../../wrappers/use-file';
import { useTranslation } from 'react-i18next';
import { addServiceRecordStyles, hiddenInputStyles, uploadIconStyles } from './AddServiceRecord.styles';

const AddServiceRecord: React.FC = () => {
    const { vin } = useParams<{ vin: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { get: getVehicle } = useVehicle();
    const { create: createService } = useVehicleService();
    const settingLists = useSettingLists();
    const { create: createFile } = useFile();

    const [serviceTypes, setServiceTypes] = useState<SettingListItem[]>([]);
    const [loadingLists, setLoadingLists] = useState(true);
    const [currentOdometer, setCurrentOdometer] = useState<{ km: number; mth: number } | null>(null);
    const [vehicleModel, setVehicleModel] = useState('');
    const [vehicleGarageUid, setVehicleGarageUid] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [photos, setPhotos] = useState<File[]>([]);

    const [formData, setFormData] = useState({
        serviceType: '',
        date: new Date().toISOString().split('T')[0],
        mileageKm: '',
        mileageMth: '',
        description: '',
    });

    useEffect(() => {
        settingLists.list('serviceTypes')
            .then((items) => setServiceTypes(items.filter((i) => !i.blocked)))
            .catch((err) => {
                console.error('Failed to load service types:', err);
                setError(t('addServiceRecord.errorLoadingServiceTypes'));
            })
            .finally(() => setLoadingLists(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!vin) return;
        getVehicle(vin).then((vehicle) => {
            if (!vehicle) return;
            setVehicleModel(vehicle.model || vehicle.vin);
            setVehicleGarageUid(vehicle.garageUid);
            if (vehicle.odometer) {
                setCurrentOdometer({ km: vehicle.odometer.kilometers, mth: vehicle.odometer.motohours });
                setFormData((prev) => ({
                    ...prev,
                    mileageKm: vehicle.odometer!.kilometers.toString(),
                    mileageMth: vehicle.odometer!.motohours.toString(),
                }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin]);

    const mileageKmNum = Number(formData.mileageKm);
    const mileageMthNum = Number(formData.mileageMth);
    const kmError = currentOdometer !== null && formData.mileageKm !== '' && mileageKmNum < currentOdometer.km;
    const mthError = currentOdometer !== null && formData.mileageMth !== '' && mileageMthNum < currentOdometer.mth;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !vin) return;
        if (kmError || mthError) return;
        if (!user.dealerId) {
            setError(t('addServiceRecord.missingDealerId'));
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const service = await createService({
                createdBy: user.uid,
                vehicleUid: vin,
                dealerId: user.dealerId,
                serviceType: formData.serviceType as VehicleServiceType,
                status: VehicleServiceStatus.Completed,
                description: formData.description,
                preferredDate: formData.date,
                ...(formData.mileageKm && { currentMileage: mileageKmNum }),
                ...(formData.mileageMth && { currentHours: mileageMthNum }),
            });

            await Promise.all(photos.map((file) =>
                createFile(file, {
                    vehicleUid: vin,
                    vehicleServiceUid: service.id,
                    dealerId: user.dealerId,
                    ...(vehicleGarageUid && { garageUid: vehicleGarageUid }),
                }),
            ));

            navigate(`/app/service-desk/vehicles/${vin}`);
        } catch (err) {
            setError(t('addServiceRecord.error'));
            console.error('Error saving service record:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingLists) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={addServiceRecordStyles.root}>
            <Typography variant="h5" fontWeight={700}>
                {t('addServiceRecord.title', { model: vehicleModel || vin })}
            </Typography>

            <Paper elevation={0} sx={addServiceRecordStyles.paper}>
                <Box component="form" onSubmit={handleSubmit} sx={addServiceRecordStyles.form}>
                    {!user?.dealerId && <Alert severity="warning">{t('addServiceRecord.missingDealerId')}</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}

                    <FormControl fullWidth required>
                        <InputLabel>{t('addServiceRecord.serviceTypeLabel')}</InputLabel>
                        <Select
                            label={t('addServiceRecord.serviceTypeLabel')}
                            value={formData.serviceType}
                            onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                        >
                            {serviceTypes.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label={t('addServiceRecord.dateLabel')}
                        type="date"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />

                    <Box sx={addServiceRecordStyles.mileageRow}>
                        <TextField
                            label={t('addServiceRecord.kmLabel')}
                            type="number"
                            fullWidth
                            placeholder="0"
                            value={formData.mileageKm}
                            onChange={(e) => setFormData({ ...formData, mileageKm: e.target.value })}
                            inputProps={{ min: 0 }}
                            error={kmError}
                            helperText={kmError ? t('addServiceRecord.kmError', { min: currentOdometer!.km }) : undefined}
                        />
                        <TextField
                            label={t('addServiceRecord.mthLabel')}
                            type="number"
                            fullWidth
                            placeholder="0"
                            value={formData.mileageMth}
                            onChange={(e) => setFormData({ ...formData, mileageMth: e.target.value })}
                            inputProps={{ min: 0 }}
                            error={mthError}
                            helperText={mthError ? t('addServiceRecord.mthError', { min: currentOdometer!.mth }) : undefined}
                        />
                    </Box>

                    <TextField
                        label={t('addServiceRecord.descriptionLabel')}
                        multiline
                        rows={3}
                        required
                        fullWidth
                        placeholder={t('addServiceRecord.descriptionPlaceholder')}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />

                    <Box>
                        <Typography variant="body2" fontWeight={500} mb={1.5}>{t('addServiceRecord.photosTitle')}</Typography>
                        {photos.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                {photos.map((file, i) => (
                                    <Chip
                                        key={i}
                                        label={file.name}
                                        size="small"
                                        onDelete={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                                        deleteIcon={<X size={14} />}
                                    />
                                ))}
                            </Box>
                        )}
                        <Box
                            component="label"
                            htmlFor="photos-upload"
                            sx={addServiceRecordStyles.uploadArea}
                        >
                            <FileUp size={40} color="#bdbdbd" style={uploadIconStyles} />
                            <Typography variant="body2" color="primary.main" fontWeight={500}>{t('addServiceRecord.uploadButton')}</Typography>
                            <Typography variant="caption" color="text.disabled">{t('addServiceRecord.uploadHint')}</Typography>
                            <input
                                id="photos-upload"
                                type="file"
                                multiple
                                style={hiddenInputStyles}
                                onChange={(e) => {
                                    if (e.target.files) {
                                        setPhotos((prev) => [...prev, ...Array.from(e.target.files!)]);
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={addServiceRecordStyles.actions}>
                        <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>{t('addServiceRecord.cancel')}</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !formData.serviceType || kmError || mthError || !user?.dealerId}
                        >
                            {submitting ? <CircularProgress size={22} color="inherit" /> : t('addServiceRecord.submit')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default AddServiceRecord;
